import { useEffect, useState } from "react";

// Shape of analysis.json written by the nublu analyzer (song_analyze/analyze.py)
// and fetched here via the short-lived presigned analysisUrl. Only the fields
// the tuning UI needs are typed.
export interface IGridTick {
  time_ms: number;
  bar: number;
  beat_in_bar: number;
  kind: "beat" | "downbeat";
  onset_strength: number;
}

export interface ISongAnalysis {
  duration_ms: number;
  tempo_bpm: number;
  beat_period_ms: number;
  first_beat_ms: number;
  downbeat_phase_offset: number;
  drop_candidates_ms: number[];
  grid: IGridTick[];
}

// Fetches + parses analysis.json from the presigned B2 GET. Requires a B2 CORS
// rule allowing GET from the app origin (deploy prereq).
export const useSongAnalysis = (analysisUrl?: string | null) => {
  const [analysis, setAnalysis] = useState<ISongAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!analysisUrl) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(analysisUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load analysis (${res.status})`);
        return res.json();
      })
      .then((data: ISongAnalysis) => {
        if (cancelled) return;
        setAnalysis(data);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Could not load analysis");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [analysisUrl]);

  return { analysis, loading, error };
};
